import type {MessageDescriptor} from 'react-intl';

type Messages = {
    [key: string]: MessageDescriptor | Messages;
};

const isLikelyMessageKey = (key: string) => {
    return key !== 'length' && /^[a-zA-Z0-9_.-]+$/.test(key);
};

const isMessageDescriptor = (value: unknown): value is MessageDescriptor => {
    if (value && typeof value === 'object' && 'defaultMessage' in value && 'id' in value) {
        return true;
    }

    return false;
};

/**
 * Proxy for react-intl message descriptor collections to throw an actionable
 * error when an attempt is made to access a non-existent key. This would
 * ordinarily throw an unhelpful error in react-intl:
 *
 *   Uncaught Error: [@formatjs/intl] An `id` must be provided to format a
 *   message.
 *
 * The Proxy will instead log an error that identifies the relevant key so the
 * the bug can be more easily addressed. The erroneous access will be allowed to
 * proceed and the react-intl error will still be thrown. This is to avoid newly
 * throwing errors in cases where the calling code checks the resulting message
 * before using it with react-intl and would not have triggered the error.
 */
const getMessagesProxy = <T extends Messages>(messages: T): T => {
    return new Proxy(messages, {
        get: (target, prop, receiver) => {
            // ignore non-string props e.g. built-in symbols and special props
            // like React's $$typeof
            if (typeof prop === 'string' && isLikelyMessageKey(prop) && !(prop in target)) {
                console.error(new TypeError(`Attempted to access non-existent message descriptor with key: "${prop}"`));
            }

            const value = Reflect.get(target, prop, receiver);

            // recurse to handle nested messages
            if (value && typeof value === 'object' && !isMessageDescriptor(value)) {
                return getMessagesProxy(value);
            }

            return value;
        },
    }) as T;
};

export default getMessagesProxy;
